import {
  Button,
  Flex,
  Loader,
  Modal,
  ScrollArea,
  Switch,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { type IUser } from "src/components/shared/UserList/UserList.types";
import AuthorizationModal, {
  type UserSelectItem,
} from "src/generator/_Modals/AuthorizationModal/AuthorizationModal";
import { refreshLogoutTimer } from "src/generator/utils/refreshLogoutTimer";
import { useGetApi, useIsNewEntry, usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { refetchLogbook } from "src/redux/slices/generatorSlice";
import { getUserId, getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import { buildRotationChanges } from "./CrewRotationModal.utils";

interface Props {
  opened: boolean;
  close: () => void;
}

const ALL_USERS_STATUS = "0";

const CrewRotationModal = ({ opened, close }: Props) => {
  const dispatch = useDispatch();
  const { post } = usePostApi();
  const isNewEntry = useIsNewEntry();
  const lbid = useSelector(state => state.generatorSlice.entryDataToSave.lbid);
  const lbeid = useSelector(state => state.generatorSlice.latestEntry.id);
  const { fullName, usertype } = useSelector(state => state.permsSlice);
  const [changes, setChanges] = useState<Record<string, boolean>>({});
  const [authOpened, { open: openAuth, close: closeAuth }] =
    useDisclosure(false);
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: users,
    refetch,
    isLoading,
  } = useGetApi<IUser[]>({
    request: "user-search-yacht",
    body: {
      _st: ALL_USERS_STATUS,
      params: { search: "", yachtid: getYachtId() },
    },
    mapper: data =>
      ((data?.list ?? []) as IUser[]).filter(user => !Number(user.deleted)),
    fetchOnInit: false,
    cache: false,
  });

  useEffect(() => {
    if (!opened) return;
    setChanges({});
    void refetch();
  }, [opened, refetch]);

  const { active, inactive } = useMemo(
    () => buildRotationChanges(users ?? [], changes),
    [users, changes],
  );
  const hasChanges = active.length > 0 || inactive.length > 0;

  const authUsers: UserSelectItem[] = useMemo(
    () => [{ label: fullName, value: getUserId(), usertype }],
    [fullName, usertype],
  );

  const handleSave = async (params: any) => {
    setIsSaving(true);
    await post({
      request: "update-users-status",
      body: {
        params: {
          ...(params.pass
            ? { pass: params.pass }
            : params.pin
              ? { pin: params.pin }
              : {}),
          active,
          inactive,
          source: "logbook",
          lbid,
          ...(!isNewEntry && { lbeid }),
        },
      },
      onSuccess: () => {
        Notification("Success", "Crew rotation updated", "green");
        closeAuth();
        close();
        refreshLogoutTimer();
        if (!isNewEntry) dispatch(refetchLogbook());
      },
    });
    setIsSaving(false);
  };

  return (
    <>
      <Modal opened={opened} onClose={close} title="Crew Rotation" size="lg">
        <Text size="sm" c="dimmed" mb={10}>
          Set crew going on rotation/vacation to Inactive, and returning crew to
          Active.
        </Text>
        {isLoading && !users ? (
          <Flex justify="center" py={20}>
            <Loader size="sm" />
          </Flex>
        ) : (
          <ScrollArea.Autosize mah="55dvh">
            {(users ?? []).map(user => {
              const checked =
                changes[user.userid] ?? Boolean(Number(user.active));
              return (
                <Flex
                  key={user.userid}
                  justify="space-between"
                  align="center"
                  py={6}>
                  <Text size="sm">
                    {user.firstname} {user.surname}
                    <Text span c="dimmed" size="xs" ml={6}>
                      {user.rolename}
                    </Text>
                  </Text>
                  <Switch
                    label={checked ? "Active" : "Inactive"}
                    labelPosition="left"
                    checked={checked}
                    onChange={e => {
                      const next = e.currentTarget.checked;
                      setChanges(prev => ({ ...prev, [user.userid]: next }));
                    }}
                  />
                </Flex>
              );
            })}
          </ScrollArea.Autosize>
        )}
        <Flex justify="flex-end" gap={10} mt={20}>
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Button disabled={!hasChanges} onClick={openAuth}>
            Save
          </Button>
        </Flex>
      </Modal>
      <AuthorizationModal
        opened={authOpened}
        close={closeAuth}
        onSubmit={handleSave}
        loading={isSaving}
        users={authUsers}
        isEntry={false}
      />
    </>
  );
};

export default CrewRotationModal;
